import {IikoMenuSettings} from "migration/entities/iikoMenuSettings";
import {IikoNomenclature} from "migration/entities/iikoNomenclature";
import {IikoExternalMenuItem} from "migration/entities/iikoExternalMenu";

// Which request filled each cached IIKO source. The fetch actions set a key on
// success and clear it on failure; iikoBindingSources compares it with the menu
// being edited. A key never outlives a failed or replaced fetch, so the store
// can hold another menu's data without it ever reaching a binding.
export interface IikoLoadedFor {
    menuSettings: string | null
    nomenclature: string | null
    externalMenuItems: string | null
}

export const menuSettingsKey = (menuId: string): string => menuId;

export const nomenclatureKey = (iikoOrganizationId: string): string => iikoOrganizationId;

export const externalMenuKey = (iikoOrganizationId: string, iikoExternalMenuId: string, iikoPriceCategoryId?: string | null): string =>
    [iikoOrganizationId, iikoExternalMenuId, iikoPriceCategoryId || ""].join("|");

type ExternalMenuItems = { [iikoProductId: string]: IikoExternalMenuItem };

// A null source is not loaded for the menu; only the one the settings name is
// ever filled.
export interface IikoBindingSources {
    settings: IikoMenuSettings | null
    nomenclature: IikoNomenclature | null
    externalMenuItems: ExternalMenuItems | null
}

const NO_SOURCES: IikoBindingSources = {settings: null, nomenclature: null, externalMenuItems: null};

// iikoBindingSources returns the IIKO data a dish on menuId may be bound with:
// the menu's settings, plus the one product source they name — the external
// menu when there is one, as on the guest page, otherwise nomenclature. A piece
// loaded for another menu, or whose fetch failed, is left out. Binding saves the
// chosen product id and the settings' whole bindings map, so a stale piece would
// write another menu's data.
export function iikoBindingSources(
    iiko: {
        iikoLoadedFor: IikoLoadedFor
        iikoMenuSettings: IikoMenuSettings
        iikoNomenclature: IikoNomenclature
        iikoExternalMenuItems: ExternalMenuItems
    },
    menuId: string | null | undefined,
): IikoBindingSources {
    const settings = iiko.iikoMenuSettings;
    if (!menuId || iiko.iikoLoadedFor.menuSettings !== menuSettingsKey(menuId) || !settings?.iikoOrganizationId) {
        return NO_SOURCES;
    }
    if (settings.iikoExternalMenuId) {
        const key = externalMenuKey(settings.iikoOrganizationId, settings.iikoExternalMenuId, settings.iikoPriceCategoryId);
        return {
            settings,
            nomenclature: null,
            externalMenuItems: iiko.iikoLoadedFor.externalMenuItems === key ? iiko.iikoExternalMenuItems : null,
        };
    }
    return {
        settings,
        nomenclature: iiko.iikoLoadedFor.nomenclature === nomenclatureKey(settings.iikoOrganizationId) ? iiko.iikoNomenclature : null,
        externalMenuItems: null,
    };
}

export interface BindableIikoProduct {
    id: string
    name: string
    prices: string
}

// bindableIikoProducts lists the products a dish can be bound to, from the
// source the settings name. Products already bound to a dish are left out.
export function bindableIikoProducts(sources: IikoBindingSources): BindableIikoProduct[] {
    const bound = new Set(Object.values(sources.settings?.productBindings || {}));
    if (sources.settings?.iikoExternalMenuId) {
        return Object.values(sources.externalMenuItems || {})
            .filter(item => !bound.has(item.itemId))
            .map(item => ({
                id: item.itemId,
                name: item.name,
                prices: (item.itemSizes || []).map(size => size.prices?.[0]?.price || 0).join(", "),
            }));
    }
    return Object.entries(sources.nomenclature?.products || {})
        .filter(([productId]) => !bound.has(productId))
        .map(([productId, product]) => ({
            id: productId,
            name: product?.data?.name,
            prices: (product?.data?.sizePrices || []).map(size => size.price?.currentPrice || 0).join(", "),
        }));
}

export interface IikoProductSummary {
    name: string
    price: number
}

// The guest page drops a size without a usable price rather than pricing it at 0.
const cheapest = (prices: (number | undefined)[]): number => {
    const usable = prices.filter((price): price is number => Number.isFinite(price) && (price as number) >= 0);
    return usable.length ? Math.min(...usable) : 0;
};

// iikoProductSummary reads a product from the source the settings name, or
// returns null when the product is not there. The price is the guest page's
// headline before required modifiers: the cheapest size it offers, 0 if none.
export function iikoProductSummary(sources: IikoBindingSources, iikoProductId: string | undefined): IikoProductSummary | null {
    if (!iikoProductId) return null;
    if (sources.settings?.iikoExternalMenuId) {
        const item = sources.externalMenuItems?.[iikoProductId];
        if (!item) return null;
        return {name: item.name, price: cheapest((item.itemSizes || []).map(size => size?.prices?.[0]?.price))};
    }
    const product = sources.nomenclature?.products?.[iikoProductId];
    if (!product) return null;
    const offered = (product.data?.sizePrices || []).filter(size => size?.price?.isIncludedInMenu !== false);
    return {name: product.data?.name, price: cheapest(offered.map(size => size?.price?.currentPrice))};
}

// The source the settings name has loaded for the menu.
const sourceLoaded = (sources: IikoBindingSources): boolean =>
    !!(sources.settings?.iikoExternalMenuId ? sources.externalMenuItems : sources.nomenclature);

// iikoDishPrice is the price a guest sees for a dish on an IIKO menu. "hidden"
// means the guest page leaves the dish out: it is unbound, or its product is not
// in the menu's IIKO source. Null means there is no IIKO price to show: the menu
// has no IIKO settings, or its source has not loaded for the menu.
export function iikoDishPrice(sources: IikoBindingSources, itemId: string): number | "hidden" | null {
    if (!sources.settings || !sourceLoaded(sources)) return null;
    return iikoProductSummary(sources, sources.settings.productBindings?.[itemId])?.price ?? "hidden";
}

// dishesWithMissingProduct returns the dishes bound to a product that is not in
// the source the settings name; the guest page hides them. Unbound dishes are
// left out. Null until both the source and the dishes have loaded for the menu.
export function dishesWithMissingProduct<Dish extends { id: string }>(sources: IikoBindingSources, dishes: Dish[] | null): Dish[] | null {
    const settings = sources.settings;
    if (!settings || !sourceLoaded(sources) || !dishes) return null;
    return dishes.filter(dish => {
        const productId = settings.productBindings?.[dish.id];
        return !!productId && !iikoProductSummary(sources, productId);
    });
}
